"use client";
import type { AuditImageSectionProps } from "../../../types/audit-image";
import type { AuditImageCardProps } from "../../../types/audit-image";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  AlertCircle,
  Camera,
  Eye,
  Loader2,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { OfflineImage } from "../../shared/offline-image";
import { Alert, AlertDescription } from "../../ui/alert.jsx";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import {
  OptimizedCheckbox,
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";
import { ImageCapturePicker } from "./image-capture-picker";
import { LocalSectionImportControl } from "./local-section-import-control";

interface GridConnectionFormProps extends AuditFormProps {
  siteId: string;
  templateId: string;
  reportId: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface GridConnectionImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface GridConnectionData {
  floodRiskPresent?: boolean;
  gridConnectionRemarks?: string;
  // Only the provided fields
  distanceToInterconnection: string;
  cableRouteConstraints: string;
  voltageLevel: string;
  utilityCompanyConditions: string;
  substationProvider: string;
  transformerDetails: string;
  switchgearDetails: string;
  meteringSystem: string;
  groundingLightningProtection: string;
  acMvConnections: string;
  substationFloodRisk: boolean;
  substationLocation: string;
  remarks: string;

  // Images
  gridConnectionImages: GridConnectionImage[];
}

const initialData: GridConnectionData = {
  distanceToInterconnection: "",
  cableRouteConstraints: "",
  voltageLevel: "",
  utilityCompanyConditions: "",
  substationProvider: "",
  transformerDetails: "",
  switchgearDetails: "",
  meteringSystem: "",
  groundingLightningProtection: "",
  substationFloodRisk: false,
  acMvConnections: "",
  substationLocation: "",
  remarks: "",
  gridConnectionImages: [],
};

const voltageLevelOptions = [
  { value: "lv", label: "LV (Low Voltage)" },
  { value: "mv", label: "MV (Medium Voltage)" },
  { value: "hv", label: "HV (High Voltage)" },
];

const progressFields = ["utilityCompanyConditions"];

const calculateProgress = (formData: GridConnectionData = initialData) => {
  const fields = progressFields;
  const filledCount = fields.filter((field) => {
    const value = formData[field as keyof GridConnectionData];

    if (typeof value === "boolean") {
      return true;
    }

    return typeof value === "string"
      ? value.trim() !== ""
      : Array.isArray(value)
        ? value.length > 0
        : value !== null && value !== undefined;
  }).length;

  return Math.round((filledCount / fields.length) * 100);
};

export const GridConnectionForm = memo(function GridConnectionForm({
  siteId,
  templateId,
  reportId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: GridConnectionFormProps) {
  const { notify } = useNotification();

  const { loading } = useAppSelector((state) => state?.files);
  const [formData, setFormData] = useState<GridConnectionData>(initialData);
  const formDataRef = useLatest(formData);
  const [selectedImage, setSelectedImage] =
    useState<GridConnectionImage | null>(null);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: boolean;
  }>({});

  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const dispatch = useAppDispatch();

  // Fetch existing files
  useEffect(() => {
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [dispatch, siteId, reportId]);

  // Load version data when viewing historical version
  const currentSiteData = useVersionAwareSiteData(readOnly);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:grid-connection` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once local form state matches the confirmed server record, the
  // IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    const prog = calculateProgress(formData);
    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        "grid-connection-information": prog,
      },
    }));
  }, [formData, setSessionProgress, templateId]);

  // Save handler
  const handleSave = useCallback(
    async (data: GridConnectionData) => {
      try {
        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: { gridConnectionData: data },
            }),
          ),
        );
        const config = notificationHelpers.formSaveSuccess("Grid Connection", [
          mutationResult1,
        ]);
        notify(config.message, config.type, config.options);
      } catch (error) {
        console.error("Save failed:", error);
        const config = notificationHelpers.formSaveError(
          "Grid Connection",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [siteId, dispatch, notify],
  );

  // Debounced save
  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly,
  });

  useEffect(() => {
    if (currentSiteData) {
      const newFormData = {
        ...formDataRef.current,
        ...(currentSiteData?.gridConnectionData || {}),
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
    }
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult2 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  // Form validation
  const isFormValid = useMemo(() => {
    return formData.utilityCompanyConditions.trim() !== "" ? true : false;
  }, [formData]);

  // Field change handler
  const handleFieldChange = useCallback((field: keyof GridConnectionData) => {
    return (value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };
  }, []);

  // Image upload handler
  const handleImageUpload = useCallback(
    (files: FileList | File[] | null, filterData) => {
      if (!files) return;
      const fileArray = Array.from(files);

      const uploadKey = JSON.stringify(filterData);
      setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId,
              entityType: "grid-connection-information",
              entityId: reportId,
              reportId,
            },
            filterData,
          }),
        ),
      )
        .then((mutationOutcome) => {
          // Show success notification for each uploaded file
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          // Show error notification
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })

        .finally(() => {
          setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
        });
    },
    [siteId, reportId, dispatch, notify],
  );

  // Update image description — updates local state; debounced save handles the dispatch
  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  // Remove image handlers
  const handleRemoveImage = useCallback((imageId: string) => {
    setImageToDelete(imageId);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, dispatch, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Get loading status
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  // Check if uploading for a section
  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingImages[uploadKey] || false;
  };

  // Image Card Component
  const ImageCard = useStableMemoComponent<
    AuditImageCardProps<GridConnectionImage>
  >(
    ({
      image,
      onView,
      onRemove,
      onDescriptionUpdate,
      isDeleting,
      imageDescription,
    }) => (
      <div key={image.id} className="relative group">
        <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
          <OfflineImage
            fileId={image?.id}
            src={image?.fileUrl}
            alt="Grid connection image"
            className="w-full h-full object-cover"
          />
          {loading && (isDeleting === true || isDeleting === image.id) && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
            onClick={() => onView(image)}
            disabled={loading}
            title="View Image"
          >
            <Eye className="w-3 h-3" />
          </Button>
          {!readOnly && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onRemove(image.id)}
              disabled={loading}
              title="Delete Image"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate truncate">
              {image?.originalName}
            </span>
            <Badge variant="outline" className="text-xs">
              {formatFileSize(image?.fileSize)}
            </Badge>
          </div>
          <OptimizedTextarea
            id={`desc-${image.id}`}
            label=""
            value={imageDescription}
            readOnly={readOnly}
            onChange={(value) => onDescriptionUpdate(image.id, value)}
            placeholder="Describe this grid connection feature..."
            rows={2}
            disabled={loading}
          />
          <div className="text-xs text-slate-500 dark:text-muted-foreground">
            Date: {new Date(image.uploadDate).toLocaleDateString()}
          </div>
        </div>
      </div>
    ),
    (prevProps, nextProps) => {
      const propsEqual =
        prevProps.image.id === nextProps.image.id &&
        prevProps.imageDescription === nextProps.imageDescription &&
        prevProps.isDeleting === nextProps.isDeleting;
      return propsEqual;
    },
  );

  // Image Section Component - FIXED SYNTAX
  const ImageSection = useStableMemoComponent<AuditImageSectionProps>(
    function ImageSection({
      title,
      filterData,
      uploadFilterData,
      emptyMessage,
      buttonText = "Add Images",
    }) {
      const sectionImages = files?.filter(
        (file) =>
          file?.relatedTo?.filterData &&
          JSON.stringify(file?.relatedTo?.filterData) ===
            JSON.stringify(filterData),
      );

      const isUploading = isUploadingForSection(uploadFilterData);

      return (
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{title}</h4>
            {!readOnly && (
              <ImageCapturePicker
                buttonText={buttonText}
                isUploading={isUploading}
                disabled={loading}
                onConfirm={(selectedFiles) =>
                  handleImageUpload(selectedFiles, uploadFilterData)
                }
              />
            )}
          </div>

          {isUploading && (
            <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="text-center">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
                <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
              </div>
            </div>
          )}

          {!isUploading && sectionImages.length === 0 ? (
            <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                    No images added
                  </p>
                  <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sectionImages.map((image) => (
                <ImageCard
                  key={image.id}
                  imageDescription={
                    imageDescriptions[image.id] ?? image?.fileDescription ?? ""
                  }
                  image={image}
                  onView={setSelectedImage}
                  onRemove={handleRemoveImage}
                  onDescriptionUpdate={updateImageDescription}
                  isDeleting={loading ? imageToDelete : false}
                />
              ))}
            </div>
          )}
        </div>
      );
    },
  );

  return (
    <div className="space-y-4 md:space-y-6 relative">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="grid-connection-information"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {/* Floating save section - hidden when viewing read-only version */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      {/* Grid Connection Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Grid Connection Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="distanceToInterconnection"
              label="Distance to interconnection (m)"
              value={formData.distanceToInterconnection}
              onChange={handleFieldChange("distanceToInterconnection")}
              placeholder="Enter distance in meters"
              type="number"
              step="0.1"
              readOnly={readOnly}
            />
            <OptimizedSelect
              id="voltageLevel"
              label="Voltage level"
              value={formData.voltageLevel}
              onChange={handleFieldChange("voltageLevel")}
              placeholder="Select voltage level"
              options={voltageLevelOptions}
              readOnly={readOnly}
            />
          </div>

          <OptimizedTextarea
            id="cableRouteConstraints"
            label="Cable route constraints and approvals"
            value={formData.cableRouteConstraints}
            onChange={handleFieldChange("cableRouteConstraints")}
            placeholder="Describe any physical, access, or approval constraints along the cable route..."
            rows={3}
            readOnly={readOnly}
          />

          <OptimizedTextarea
            id="utilityCompanyConditions"
            label="Utility company conditions and requirements to be met"
            value={formData.utilityCompanyConditions}
            onChange={handleFieldChange("utilityCompanyConditions")}
            placeholder="List any utility-specific requirements, approvals, technical constraints, or conditions to be met..."
            rows={3}
            required
            readOnly={readOnly}
          />
          <p className="text-sm text-slate-500 -mt-2 dark:text-muted-foreground">
            Include any utility-specific approvals, technical conditions, or
            compliance requirements that apply to this connection.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="substationProvider"
              label="Substation provider"
              value={formData.substationProvider}
              onChange={handleFieldChange("substationProvider")}
              placeholder="Name the provider or owner of the substation connection"
              readOnly={readOnly}
            />
            <OptimizedInput
              id="transformerDetails"
              label="Transformer details"
              value={formData.transformerDetails}
              onChange={handleFieldChange("transformerDetails")}
              placeholder="Enter transformer rating, type, or connection notes"
              readOnly={readOnly}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OptimizedInput
              id="switchgearDetails"
              label="Switchgear details"
              value={formData.switchgearDetails}
              onChange={handleFieldChange("switchgearDetails")}
              placeholder="Enter switchgear type, rating, or installation notes"
              readOnly={readOnly}
            />
            <OptimizedInput
              id="meteringSystem"
              label="Metering system"
              value={formData.meteringSystem}
              onChange={handleFieldChange("meteringSystem")}
              placeholder="Describe the metering arrangement or equipment used"
              readOnly={readOnly}
            />
          </div>

          <OptimizedInput
            id="acMvConnections"
            label="AC/MV connection details"
            value={formData.acMvConnections}
            onChange={handleFieldChange("acMvConnections")}
            placeholder="Enter the AC/MV connection arrangement, equipment, or route notes"
            readOnly={readOnly}
          />

          <OptimizedTextarea
            id="groundingLightningProtection"
            label="Grounding and lightning protection"
            value={formData.groundingLightningProtection}
            onChange={handleFieldChange("groundingLightningProtection")}
            placeholder="Describe grounding arrangements, surge protection, or lightning mitigation measures..."
            rows={3}
            readOnly={readOnly}
          />

          <OptimizedInput
            id="substationLocation"
            label="Nearest substation location"
            value={formData.substationLocation || ""}
            onChange={handleFieldChange("substationLocation")}
            placeholder="Describe the location or distance to the nearest substation"
            readOnly={readOnly}
          />

          <OptimizedCheckbox
            id="floodRiskPresent"
            label="Is flood risk present at the substation or connection point?"
            checked={formData.floodRiskPresent}
            onChange={handleFieldChange("floodRiskPresent")}
            description="Tick if flood risk is present"
          />

          <OptimizedTextarea
            id="gridConnectionRemarks"
            label="Other remarks"
            value={formData.gridConnectionRemarks || ""}
            onChange={handleFieldChange("gridConnectionRemarks")}
            rows={3}
            placeholder="Enter any additional grid connection notes, issues, or observations"
            readOnly={readOnly}
          />

          <ImageSection
            title="Grid Connection Images"
            filterData={{ grid_connection: true }}
            uploadFilterData={{ grid_connection: true }}
            emptyMessage="Add images related to grid connection"
            buttonText="Add Images"
          />
        </CardContent>
      </Card>

      {!isFormValid && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please fill in all required fields to complete this section.
          </AlertDescription>
        </Alert>
      )}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant={isFormValid ? "default" : "secondary"}>
              {isFormValid ? "Complete" : "Incomplete"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress?.[templateId]?.["grid-connection-information"] ||
                calculateProgress(formData)}
              %
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
